/**
 * Dealer Selector
 */
import { GMXGoogleSearchAddress } from '../google-search-address/google-search-address'
import { DealerDepartment, DealerModel } from '../../models/dealer.model'
import { PanelDataModel } from '../../models/panel.model'
import { GMXGoogleAutocompleteSearchDetail } from '../../models/google-search-address.model'
import { getValueOfQueryParam } from '../../utils/query-params'
import { sanitizeInput, sanitizeUrl } from '../../utils/sanitize'
import { BRAND_MAKE_CODES, setGbaalDealerAttr } from '../../utils/gbaalDealerData'
import { extractPostalCode } from '../../utils/postalCode'
import { persistSearchedZip, whenLocationSingletonReady } from '../../utils/locationSingleton'

/**
 * Searching for this term selects the test BAC below instead of querying the dealer API, so leads
 * can be submitted end-to-end against the lead service without reaching a real dealer. The test BAC
 * has no record in the dealer API, so its row is synthesized rather than fetched.
 */
const TEST_DEALER_SEARCH_TERM = '00000'
const TEST_DEALER_BAC = '246435'

export class GMXDealerSelector extends HTMLElement {
	parentPanelEl: HTMLElement
	googleSearchAddrEl: GMXGoogleSearchAddress
	searchPanelEl: HTMLElement
	resultsPanelEl: HTMLElement
	dealerListEl: HTMLElement | undefined

	static observedAttributes = ['data-preselected-dealer-bac']

	private boundSearchInputSearchHandler = this._handleSearchInputSearch.bind(this)
	private boundDealerListClickHandler = this._handleDealerListClick.bind(this)

	private placesService: google.maps.places.PlacesService

	/**
	 * Set the moment the visitor searches for themselves.  Seeding resolves the stored location
	 * asynchronously and can land after they have already searched, and re-running the stored
	 * zip at that point would throw away the search they are looking at.
	 */
	private visitorHasSearched = false

	/** Bumped on every disconnect, so work deferred by a connection that has since ended can tell. */
	private connectionGeneration = 0

	get dealerApi(): string {
		return this.getAttribute('data-dealer-api-url')
	}

	get brands(): string {
		return this.getAttribute('data-brands')
	}
	set brands(val: string) {
		this.setAttribute('data-brands', val)
	}

	get serviceCodeFilter(): string {
		return this.getAttribute('data-service-code-filter') || ''
	}
	set serviceCodeFilter(val: string) {
		this.setAttribute('data-service-code-filter', val)
	}

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}
	set labelData(val: Record<string, string>) {
		this.setAttribute('data-labels', JSON.stringify(val))
	}

	get preselectedDealerBac(): string {
		return this.getAttribute('data-preselected-dealer-bac') || ''
	}
	set preselectedDealerBac(val: string) {
		this.setAttribute('data-preselected-dealer-bac', val)
	}

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (this.isConnected && name === 'data-preselected-dealer-bac' && newValue && oldValue !== newValue) {
			this.preSelectDealer(newValue)
		}
	}

	connectedCallback(): void {
		this.parentPanelEl = this.closest('gmx-panel')
		this.googleSearchAddrEl = this.querySelector('gmx-google-search-address') as GMXGoogleSearchAddress
		this.searchPanelEl = this.querySelector('.search-panel')
		this.resultsPanelEl = this.querySelector('.results-panel')

		// There are three ways to preselect the dealer: 1.) via data attribute on element,
		// 2.) via query string parameter or 3.) via a selection the surrounding panel recorded.
		const preselectedDealerParam =
			this.preselectedDealerBac || getValueOfQueryParam('bacId') || this._getPanelSelectedDealerBac()

		if (preselectedDealerParam) {
			this.preSelectDealer(preselectedDealerParam)
		} else {
			// Nothing names a dealer, so fall back to searching near wherever the visitor
			// already told the site they are.
			this._seedFromStoredLocation()
		}

		this.resultsPanelEl.addEventListener('click', this.boundDealerListClickHandler)
		this.googleSearchAddrEl.addEventListener('gmxGoogleAutocompleteSearch', this.boundSearchInputSearchHandler)
	}

	disconnectedCallback(): void {
		this.connectionGeneration++

		this.resultsPanelEl.removeEventListener('click', this.boundDealerListClickHandler)
		this.googleSearchAddrEl.removeEventListener('gmxGoogleAutocompleteSearch', this.boundSearchInputSearchHandler)
	}

	/**
	 * Dealer the surrounding panel already recorded a selection for, if there is one.
	 * @private
	 */
	private _getPanelSelectedDealerBac(): string {
		if (!this.parentPanelEl) {
			return ''
		}

		try {
			const panelData = JSON.parse(this.parentPanelEl.getAttribute('data-panel-data')) as PanelDataModel

			return panelData.selectedDealerBac || ''
		} catch {
			return ''
		}
	}

	/**
	 * Whether a dealer is spoken for, either already on screen or still being fetched.
	 *
	 * `preSelectDealer()` waits on the same Google Maps load event the seeded search does, and
	 * registers second, so a dealer named while the seed was waiting has not rendered yet when
	 * the seed runs.  Reading the attribute as well catches that dealer while it is in flight.
	 * @private
	 */
	private _hasPreselectedDealer(): boolean {
		return Boolean(this.preselectedDealerBac) || Boolean(this.dealerListEl?.hasAttribute('data-is-preselected-dealer'))
	}

	/**
	 * Whether a seeded search scheduled earlier should still go ahead.  Seeding resolves the
	 * stored location and then waits on the Google Maps API, and by the time either finishes the
	 * page may have moved on: the panel can have closed, so results would render where nobody
	 * sees them; a dealer can have been named, and results would discard that selection; the
	 * visitor can have searched for themselves, which outranks a value they gave another app; or
	 * the selector can have been detached and reattached, leaving this seed behind while the new
	 * connection starts its own.
	 * @param generation {number} - Connection the caller was scheduled under
	 * @private
	 */
	private _seedStillApplies(generation: number): boolean {
		return this.isConnected
			&& !this.visitorHasSearched
			&& !this._hasPreselectedDealer()
			&& generation === this.connectionGeneration
	}

	/**
	 * Open on dealers near the visitor's stored zip rather than an empty list. Nothing is
	 * written back — the value is already stored, and persisting is reserved for searches the
	 * visitor actually performed.
	 * @private
	 */
	private async _seedFromStoredLocation(): Promise<void> {
		const generation = this.connectionGeneration
		const locationSingleton = await whenLocationSingletonReady()
		const location = await locationSingleton.resolve()

		if (!location?.zip || !this._seedStillApplies(generation)) {
			return
		}

		if (this.googleSearchAddrEl?.inputEl) {
			this.googleSearchAddrEl.inputEl.value = location.zip
			this.googleSearchAddrEl.inputEl.classList.add('filled')
		}

		const runInitialSearch = (): void => {
			if (!this._seedStillApplies(generation)) {
				return
			}

			this.searchByAmbiguousTerm(location.zip)
		}

		if (window.gmxGoogleMapApiLoaderSingleton?.loadStatus !== 'loaded') {
			window.addEventListener('gmxGoogleMapApiLoaded', runInitialSearch, { once: true })
		} else {
			runInitialSearch()
		}
	}

	/**
	 * Callback for when the search field alerts (fires event) that a new input has been entered
	 * @param e {CustomEvent}
	 * @private
	 */
	private async _handleSearchInputSearch(e: CustomEvent<GMXGoogleAutocompleteSearchDetail>): Promise<void> {
		const details = e.detail

		this.visitorHasSearched = true

		// The test term is not a location, so it is never shared as one.
		if (details.searchStr?.trim() === TEST_DEALER_SEARCH_TERM) {
			this.selectTestDealer()
			return
		}

		if (details.location) {
			this.searchForDealers(details.location.lat, details.location.lng)

			persistSearchedZip(details.postalCode || extractPostalCode(details.searchStr), details)
		} else {
			// A typed term is dispatched before anything is geocoded, so the postal code has to
			// come out of the place the search itself resolved.  A search that resolved to
			// nothing shares nothing: the raw term is whatever the visitor happened to type,
			// and Google just said it names no real place.
			const place = await this.searchByAmbiguousTerm(details.searchStr)

			persistSearchedZip(extractPostalCode(place?.formatted_address))
		}
	}

	/**
	 * Callback for when the dealer list picked up a click.  Most likely this is a dealer in the list selected.
	 * @private
	 */
	private _handleDealerListClick(): void {
		if (!this.dealerListEl) {
			return
		}

		if (!this.dealerListEl.hasAttribute('data-is-preselected-dealer')) {
			let numDealerChecked = 0
			const checkboxEls = this.dealerListEl.querySelectorAll(
				'input[type="checkbox"]'
			) as NodeListOf<HTMLInputElement>

			checkboxEls.forEach((el: HTMLInputElement) => {
				if (el.checked) {
					numDealerChecked++
				}
			})

			checkboxEls.forEach((el: HTMLInputElement) => {
				if (numDealerChecked >= 3 && !el.checked) {
					el.setAttribute('disabled', '')
				} else {
					el.removeAttribute('disabled')
				}
			})
		}
	}

	/**
	 * Trigger a search by first geocoding the location string then trigger a search.
	 *
	 * Returns the place Google resolved the term to, so a caller acting on a visitor's own
	 * search can read a postal code out of it; `undefined` when nothing could be resolved.
	 * @param input
	 */
	async searchByAmbiguousTerm(input: string): Promise<google.maps.places.PlaceResult | undefined> {
		return await this.getPlaceFromAmbiguousTerm(input)
			.then(rslts => {
				const locationGeometry = rslts[0].geometry
				this.searchForDealers(locationGeometry.location.lat(), locationGeometry.location.lng())

				return rslts[0]
			})
			.catch((error): google.maps.places.PlaceResult | undefined => {
				this.displaySearchError(this.labelData?.genericSearchFailedMsg || 'Error message missing')
				window.console.warn(error)

				return undefined
			})
	}

	/**
	 * Try to geocode a location by random location string.
	 * @param input {string} - String value to geocode
	 */
	async getPlaceFromAmbiguousTerm(input: string): Promise<google.maps.places.PlaceResult[]> {
		if (!this.placesService) {
			const blankDiv = document.createElement('div')
			this.placesService = new google.maps.places.PlacesService(blankDiv)
		}

		return new Promise((resolve, reject) => {
			this.placesService.findPlaceFromQuery(
				{
					query: input,
					fields: ['formatted_address', 'geometry'],
				},
				(rslts, status) => {
					if (status === 'OK') {
						resolve(rslts)
					} else {
						reject(status)
					}
				}
			)
		})
	}

	/**
	 * Based on a given latitide and longitude, trigger a search for dealers
	 * @param lat {number} - Latitude of the location to search
	 * @param lng {number} - Longitude of the location to search
	 */
	async searchForDealers(lat: number, lng: number): Promise<void> {
		const headers = new Headers({
			'Content-Type': 'application/json; charset=utf-8',
			clientapplicationid: 'quantum',
			locale: `en-us`,
		})

		let makeCodes = ''
		this.brands.split(',').forEach(brand => {
			// @ts-expect-error:  Fake (and better) enum
			if (BRAND_MAKE_CODES[brand]) {
				// @ts-expect-error:  Fake (and better) enum
				makeCodes += `${BRAND_MAKE_CODES[brand]},`
			}
		})

		let qsps = `makeCodes=${makeCodes.replace(/,$/, '')}&latitude=${lat}&longitude=${lng}&searchType=latLongSearch&desiredCount=25`
		qsps += this.serviceCodeFilter ? `&serviceCodes=${this.serviceCodeFilter}` : ''

		try {
			await fetch(`${this.dealerApi}?${qsps}`, {
				method: 'GET',
				headers: headers,
				cache: 'no-cache',
			})
				.then(response => response.json())
				.then(data => {
					if (parseInt(data.resultCount, 10) > 0) {
						this.displayDealers(data.payload.dealers)
					} else {
						this.displaySearchError(this.labelData?.noDealersFoundMsg || 'Error message missing')
					}
				})
		} catch (e) {
			window.console.error(`ERROR:  Failed to retrieve dealer results: ${e}`)
			this.displaySearchError(this.labelData?.genericSearchFailedMsg || 'Error message missing')
		}
	}

	async getDealerInfoByBac(bacId: string): Promise<DealerModel> {
		const headers = new Headers({
			'Content-Type': 'application/json; charset=utf-8',
			clientapplicationid: 'quantum',
			locale: `en-us`,
		})

		const qsps = `?searchType=dealerBacSearch&dealerBAC=${bacId}`

		let dealerInfo: DealerModel

		try {
			await fetch(`${this.dealerApi}?${qsps}`, {
				method: 'GET',
				headers: headers,
				cache: 'no-cache',
			})
				.then(response => response.json())
				.then(data => {
					if (parseInt(data.resultCount, 10) > 0) {
						dealerInfo = data.payload.dealers[0]
					}
				})
		} catch (e) {
			window.console.error(`ERROR:  Failed to retrieve dealer results: ${e}`)
		}

		return dealerInfo
	}

	/**
	 * Show dealers after a given search
	 * @param dealers {DealerModel[]} - List of dealers to display
	 */
	displayDealers(dealers: DealerModel[]): void {
		this.googleSearchAddrEl.classList.remove('invalid')
		this.googleSearchAddrEl.classList.remove('has-search-error')

		this._ensureDealerListEl()

		this.dealerListEl.removeAttribute('data-is-preselected-dealer')

		this.dealerListEl.innerHTML = ''
		this.dealerListEl.appendChild(this.buildDealerListEl(dealers))
	}

	/**
	 * Create the container the dealer rows are rendered into if it doesn't exist yet.
	 * @private
	 */
	private _ensureDealerListEl(): void {
		if (!this.dealerListEl) {
			this.dealerListEl = document.createElement('div')
			this.dealerListEl.classList.add('dealer-list')
			this.dealerListEl.ariaRequired = 'true'
			this.dealerListEl.role = 'radiogroup'
			this.resultsPanelEl.appendChild(this.dealerListEl)
		}
	}

	/**
	 * Render a single dealer as the only, already checked option.  The preselected flag exempts the
	 * row from the three-dealer limit applied to search results; it stays user-toggleable.
	 * @param dealer {DealerModel} - Dealer to render
	 * @private
	 */
	private _renderPreselectedDealer(dealer: DealerModel): void {
		this._ensureDealerListEl()

		this.dealerListEl.setAttribute('data-is-preselected-dealer', '')

		this.dealerListEl.innerHTML = ''
		this.dealerListEl.appendChild(this.buildDealerListEl([dealer]))

		const checkboxEl = this.dealerListEl.querySelector('input[type="checkbox"]') as HTMLInputElement
		checkboxEl.checked = true
	}

	buildDealerListEl(dealers: DealerModel[]): HTMLElement {
		const dealerListEl = document.createElement('ul')
		dealerListEl.classList.add('gmx-text-list', 'layout-stacked', 'style-title-plus-address-checkbox')

		dealers.forEach(dealer => {
			const dealerSalesPhoneNumber = this.getDepartmentPhoneNumber(dealer, 'sales')
			const dealerListItemEl = document.createElement('li')
			dealerListItemEl.innerHTML = `
				<div class="gmx-list-item-wrapper">
					<label class="gmx-label">
						<div class="gmx-list-content">
							<div class="gmx-list-title"></div>
							<div class="gmx-list-address"></div>
							<div class="gmx-list-distance"></div>
						</div>
						<input type="checkbox" name="salesLead.dealerId[]" value="${sanitizeInput(dealer.bac)}" class="gmx-checkbox"/>
					</label>
					<div class="gmx-list-contact-info">
						<a class="gmx-btn gmx-btn-link-secondary dealer-phone-link" href=""></a>
						 ${dealer.dealerUrl ? `• <a class="gmx-btn gmx-btn-link-secondary dealer-website-link" data-gbaal-dealer-exit href="" target="_blank" rel="noopener noreferrer"></a>` : ''}
					</div>
				</div>
			`

			const titleEl = dealerListItemEl.querySelector('.gmx-list-title')
			titleEl.textContent = dealer.dealerName

			const addressEl = dealerListItemEl.querySelector('.gmx-list-address')
			addressEl.textContent = `${dealer.address.addressLine1} ${dealer.address.addressLine2} ${dealer.address.cityName}, ${dealer.address.countrySubdivisionCode} ${dealer.address.postalCode}`

			const distanceEl = dealerListItemEl.querySelector('.gmx-list-distance')
			distanceEl.textContent = `${Math.round(dealer.distance)} ${dealer.distanceProp.measurementUnit}`

			const dealerPhoneLinkEl = dealerListItemEl.querySelector('.dealer-phone-link') as HTMLAnchorElement
			dealerPhoneLinkEl.href = `tel:${dealerSalesPhoneNumber}`
			dealerPhoneLinkEl.textContent = dealerSalesPhoneNumber

			if (dealer.dealerUrl) {
				const dealerWebsiteLinkEl = dealerListItemEl.querySelector('.dealer-website-link') as HTMLAnchorElement
				dealerWebsiteLinkEl.href = sanitizeUrl(dealer.dealerUrl)
				dealerWebsiteLinkEl.textContent = this.labelData?.websiteLabel || 'Label missing'
			}

			// One attribute per row supplies the dealer data for every interaction inside it — the
			// selection checkbox as well as the phone and website links.
			setGbaalDealerAttr(dealerListItemEl, dealer)

			dealerListEl.appendChild(dealerListItemEl)
		})

		return dealerListEl
	}

	/**
	 * Reset the dealer selector to its initial empty state: no preselected dealer, no search
	 * results, cleared search field, and no error/invalid state.
	 */
	reset(): void {
		this.removeAttribute('data-preselected-dealer-bac')
		this.classList.remove('invalid')

		if (this.dealerListEl) {
			this.dealerListEl.remove()
			this.dealerListEl = undefined
		}

		this.googleSearchAddrEl?.reset()
	}

	/**
	 * Preselect a dealer based on a given BAC ID.
	 * @param dealerBac
	 */
	preSelectDealer(dealerBac: string): void {
		const googleApiMapLoadedCallback = async () => {
			const dealerInfo = await this.getDealerInfoByBac(dealerBac)

			if (dealerInfo) {
				this._renderPreselectedDealer(dealerInfo)
			}
		}

		if (window.gmxGoogleMapApiLoaderSingleton?.loadStatus !== 'loaded') {
			window.addEventListener('gmxGoogleMapApiLoaded', googleApiMapLoadedCallback, { once: true })
		} else {
			googleApiMapLoadedCallback()
		}
	}

	/**
	 * Select the test BAC so a lead can be submitted without routing to a real dealer.  Nothing is
	 * fetched: the dealer API holds no record for the test BAC, so its row is built from placeholder
	 * data.
	 */
	selectTestDealer(): void {
		this.googleSearchAddrEl.classList.remove('invalid')
		this.googleSearchAddrEl.classList.remove('has-search-error')

		// The contact details are reserved-for-fiction placeholders rather than blanks so the row's
		// phone and website links still carry link text, which an empty phone number would leave
		// unlabelled.
		this._renderPreselectedDealer({
			bac: TEST_DEALER_BAC,
			dealerCode: TEST_DEALER_BAC,
			dealerName: 'Test Dealer',
			dealerUrl: 'https://www.example.com/',
			address: {
				addressLine1: '100 Test Drive',
				addressLine2: '',
				cityName: 'Testville',
				countrySubdivisionCode: 'MI',
				postalCode: TEST_DEALER_SEARCH_TERM,
			},
			distance: 0,
			distanceProp: {
				value: 0,
				measurementUnit: 'MI',
			},
			generalContact: {
				phone1: '313-555-0100',
				phone2: '',
			},
			generalOpeningHour: [],
			serviceOpeningHour: [],
			geolocation: {
				latitude: 0,
				longitude: 0,
			},
			services: [],
			departments: [],
		})
	}

	/**
	 * Callback for when a search failed for some reason.
	 * @param errorMsg {string} - Error message to display under the input field.
	 */
	displaySearchError(errorMsg: string): void {
		if (this.googleSearchAddrEl) {
			this.googleSearchAddrEl.displayErrorMsg(errorMsg)
		}
	}

	/**
	 * Get the phone number for a given dealer and department code.
	 * @param dealer
	 * @param departmentCode
	 */
	getDepartmentPhoneNumber(dealer: DealerModel, departmentCode: string): string {
		let preferredPhone = ''

		if (dealer.departments.length) {
			const preferredContact: DealerDepartment = dealer.departments.filter(d => d.code === departmentCode)[0]
			if (preferredContact) {
				preferredPhone = preferredContact.phoneNumber
			} else {
				preferredPhone = dealer.departments[0].phoneNumber
			}
		} else {
			preferredPhone = dealer.generalContact.phone1
		}

		return preferredPhone
	}
}

if (!customElements.get('gmx-dealer-selector')) {
	customElements.define('gmx-dealer-selector', GMXDealerSelector)
}
